import React, { useState, Fragment } from 'react';
import classNames from 'classnames';

import { handleEnter } from 'common/dom_helpers/keyPressHelpers';
import { I18n } from 'common/i18n';
import { Filters, getEnabledFilters } from 'common/components/AssetBrowser/lib/helpers/enabled_filters';
import { Tab } from 'common/components/AssetBrowser/lib/constants';
import { ForgeStack } from '@tylertech/forge-react';

import ClearFilters from './clear_filters';
import { AssetTypesFilter } from './asset_types_filter';
import AuthorityFilter from './authority_filter';
import CategoryFilter from './category_filter';
import CustomFacetFilters from './custom_facet_filters';
import OwnedByFilter from './owned_by_filter';
import SourceFilter from './source_filter';
import TagFilter from './tag_filter';
import { VersionFilter } from './version_filter';
import VisibilityFilter from './visibility_filter';
import RequesterFilter from './requester';
import { FilterLookup, FilterType } from './FilterLookup';
import { CheckboxFilter } from './Checkboxes/CheckboxFilter';

interface CatalogFiltersProps {
  activeTab: Tab;
  showAwaitingApprovalFilter: boolean;
}

export const CatalogFilters = ({ activeTab, showAwaitingApprovalFilter }: CatalogFiltersProps) => {
  const [isFilterContentOpen, setIsFilterContentOpen] = useState(true);

  const handleFilterContentToggleClick = () => {
    setIsFilterContentOpen((prev) => !prev);
  };

  const filterContentClass = classNames('filter-content', { hidden: !isFilterContentOpen });

  const openFiltersButton = !isFilterContentOpen ? (
    <button
      className="open-filters filter-content-toggle"
      onClick={handleFilterContentToggleClick}
      onKeyDown={handleEnter(handleFilterContentToggleClick, true)}
      aria-label={I18n.t('shared.asset_browser.filters.desktop.expand')}
    >
      <span
        aria-hidden="true"
        className="socrata-icon-arrow-left"
        title={I18n.t('shared.asset_browser.filters.desktop.expand')}
      />
    </button>
  ) : null;

  const closeFiltersButton = isFilterContentOpen ? (
    <button
      className="close-filters filter-content-toggle"
      onClick={handleFilterContentToggleClick}
      onKeyDown={handleEnter(handleFilterContentToggleClick, true)}
    >
      {I18n.t('shared.asset_browser.filters.desktop.hide')}
      <span
        aria-hidden="true"
        className="socrata-icon-arrow-right"
        title={I18n.t('shared.asset_browser.filters.desktop.contract')}
      />
    </button>
  ) : null;

  const filterHeader = (
    <div className="catalog-filters-header">
      <ClearFilters />
      {closeFiltersButton}
    </div>
  );

  const enabledFilters = getEnabledFilters(activeTab, showAwaitingApprovalFilter);

  const checkboxFilterOrder = [
    Filters.OnlyRecentlyViewed,
    Filters.EnrolledInArchival,
    Filters.ShowInternalTargetAudience,
    Filters.ShowPublicTargetAudience,
    Filters.ApprovalStatus,
  ];

  // Render the appropriate component based on filter type
  // TODO: So far, only used for Checkbox filters. EN-85153 and EN-85150
  const getFilterComponent = (filterName: Filters): JSX.Element | null => {
    // Get filter config from CheckboxLookup
    const filterConfig = FilterLookup[filterName];

    if (!filterConfig) {
      console.warn(`No filter configuration found for ${filterName}. It needs to be added to FilterLookup.`);
      return null;
    }

    // Render component based on filter type
    switch (filterConfig.filterType) {
      case FilterType.Checkbox:
        return <CheckboxFilter filterName={filterName} />;
    }
  };

  return (
    <div className="catalog-filters">
      {openFiltersButton}

      <div className={filterContentClass}>
        {filterHeader}
        <form>
          <div className="filter-switches">
            <ForgeStack gap={0}>
              {checkboxFilterOrder.map((filterName) => {
                return (
                  enabledFilters[filterName] && (
                    <Fragment key={filterName}>{getFilterComponent(filterName)}</Fragment>
                  )
                );
              })}
            </ForgeStack>
          </div>
          {enabledFilters.version && <VersionFilter />}
          <AssetTypesFilter />
          {enabledFilters.source && <SourceFilter />}
          {enabledFilters.authority && <AuthorityFilter />}
          {enabledFilters.ownedBy && <OwnedByFilter />}
          {enabledFilters.visibility && <VisibilityFilter />}
          {enabledFilters.category && <CategoryFilter />}
          {enabledFilters.tag && <TagFilter />}
          {enabledFilters.customFacet && <CustomFacetFilters />}
          {enabledFilters.requester && <RequesterFilter />}
        </form>
      </div>
    </div>
  );
};
